import { TooltipWrapper } from "@repo/ui/components/tooltip-wrapper";
import { IconArrowNarrowRight, IconInfoCircle } from "@tabler/icons-react";
import { uniq } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import DocIcon from "@/assets/icons/doc.svg";
import { BounceLoader } from "@/components/common/bounce-loader";
import { ButtonLoadingIcon } from "@/components/common/buttons/button-loading-icon";
import { Loader } from "@/components/common/loader";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { SubItemsPricing } from "@/features/addons/types/sub-Items-pricing.type";
import { useViewItemEvent } from "@/features/analytics/hooks/use-view-item-event";
import {
  isOmnibusEnabled,
  type OmnibusPresentation,
} from "@/features/diets/helpers/omnibus";
import type { DietCatalogDetail } from "@/features/diets/types";
import { getKcalFromMeals } from "@/lib/helpers/calorifics";
import { getDeliveryDate } from "@/lib/helpers/diet-dates";
import {
  formatCateringName,
  formatDietName,
} from "@/lib/helpers/format-brand-name";
import { useDietDates } from "@/lib/hooks/use-diet-dates";
import { getLocalePrice } from "@/lib/utilities";
import type {
  ConfigureDietFormType,
  ConfigureDietReturnFormType,
} from "../../forms/configure-diet";
import { DiscountsKVParsed } from "../../helpers";
import type { CartPricing } from "../../types";
import { DeliveryCost } from "../delivery-cost";
import type { KeyValuePairProps } from "../pricing/price-element";
import { SummaryAddonsSection } from "./summary/summary-addons-section";
import { SummaryDayPriceRow } from "./summary/summary-day-price-row";
import { SummaryDietCard } from "./summary/summary-diet-card";
import { SummaryDiscountSection } from "./summary/summary-discount-section";

const getErrors = (
  form: ConfigureDietReturnFormType,
  t: ReturnType<typeof useTranslations>,
) => {
  const errors: string[] = [];

  if (form.getValues("dates")?.length === 0 && !form.formState.errors.dates) {
    errors.push(t("cart.configuration.errors.atLeastOneDate"));
  }

  if (form.getValues("dates")?.length !== form.getValues("daysToSelect")) {
    errors.push(t("cart.configuration.errors.amountOfDays"));
  }

  if (!form.getValues("kcal") && !form.formState.errors.kcal) {
    errors.push(t("cart.configuration.errors.caloricityRequired"));
  }

  if (!form.formState.isDirty) {
    errors.push(t("cart.configuration.errors.noChanges"));
  }

  return errors;
};

export const ConfigurationPricing = ({
  form,
  diet,
  pricing,
  subItemsPricing,
  loading,
  pending,
  edit,
  omnibus,
}: {
  form: ConfigureDietReturnFormType;
  diet: DietCatalogDetail;
  pricing?: CartPricing;
  // Omnibus presentation of this quote (MPL-1835).
  omnibus?: OmnibusPresentation;
  // Addon quote of the configured position. Kept apart from `pricing`, which
  // covers the diet alone.
  subItemsPricing?: SubItemsPricing | null;
  loading?: boolean;
  pending?: boolean;
  edit?: boolean;
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const isValid = form.formState.isValid;
  const isDirty = form.formState.isDirty;

  const { lastPossibleOrderDay, lastPossibleOrderDayFormatted } = useDietDates({
    diet,
    saturdays: true,
    sundays: true,
    locale,
  });

  // Diet price for the configured days — the base of the "N days × price" row
  // and of every per-day amount below.
  const dietTotal = pricing?.final.gross || 0;
  // Regular diet price, only used by the struck-through "Cena łącznie" row.
  const dietRegularTotal = pricing?.before?.gross ?? dietTotal;
  // Charged addon value of the position. Deposits stay out of it, exactly as
  // the backend keeps them out of the cart total.
  const addonsTotal = subItemsPricing?.final.gross ?? 0;
  // Regular addon value, for the same reason as `dietRegularTotal`.
  const addonsRegularTotal = subItemsPricing?.before?.gross ?? addonsTotal;
  // Addon promotions never reach `pricing.discounts` (they only show up as the
  // gap between the quoted addon before/final), so the discount row for them is
  // derived here.
  const addonsDiscount = Math.max(addonsRegularTotal - addonsTotal, 0);
  // Regular value of the position, struck through above the discount rows.
  const regularTotalPrice = dietRegularTotal + addonsRegularTotal;
  // What the position actually costs — the cart total includes addons too.
  const totalPrice = dietTotal + addonsTotal;

  const dailyPrice = dietTotal / (form.watch("dates")?.length || 1);

  // Under the Omnibus flag the per-day price is styled as a reduction, with L
  // under it, only when L is above it (MPL-1835, contract pt 7).
  const omnibusEnabled = isOmnibusEnabled();
  const omnibusPromotion =
    omnibusEnabled && omnibus?.promotion ? omnibus : undefined;
  const dailyPriceDiscounted = omnibusEnabled
    ? Boolean(omnibusPromotion)
    : dietRegularTotal > dietTotal;

  // Calories from form ( supervisoriant )
  const kcal =
    getKcalFromMeals(diet, Number(form.watch("kcal")), form.watch("meals")) ||
    form.watch("kcal");

  // Number of dates from form
  const daysQuantity = form.watch("dates")?.length || 0;

  // Errors that come from defaults (not visible in form.formState.errors on first render)
  const errorArray = getErrors(form, t);

  // Tooltip errors - concat errors from formState and errorArray + uniq (can be duplicated)
  const errors = uniq(
    Object.entries(form.formState.errors)
      .map(([_, value]) => {
        let err = "";
        if (value.message?.includes("minDays")) {
          err = t(value.message, { days: diet.catering.days.min.toString() });
        } else if (value.message?.includes("maxDays")) {
          err = t(value.message, { days: diet.catering.days.max.toString() });
        } else if (value.message?.includes("minMeals")) {
          err = t(value.message, { meals: diet.minMealTypes.toString() });
        } else if (value.message?.includes("daysAllowedToOrderInFuture")) {
          err = lastPossibleOrderDay
            ? t(value.message, {
                date: lastPossibleOrderDayFormatted,
              })
            : "";
        } else {
          err = t(value.message || "");
        }
        return err;
      })
      .concat(errorArray),
  );
  const discounts: KeyValuePairProps[] | null = DiscountsKVParsed({
    discounts: pricing?.discounts || [],
    t,
    className: "text-brand-green1-300",
    // The discount breakdown is split by what the discount applies to, so this
    // row is the diet share next to the addon one below.
    label: t("common.diet"),
    type: ["automatic"],
  });

  const discountsLength: KeyValuePairProps[] | null = DiscountsKVParsed({
    discounts: pricing?.discounts || [],
    t,
    className: "text-brand-green1-300",
    type: ["diet_length"],
  });

  // Addon share of the discount. Only listed when a promotion actually took
  // something off the addons.
  const addonsDiscountItems: KeyValuePairProps[] =
    addonsDiscount > 0
      ? [
          {
            label: t("cart.configuration.addons.summaryLabel"),
            value: `-${getLocalePrice({ price: addonsDiscount })}`,
            className: "font-normal text-brand-green1-300",
            valueClassName: "font-bold text-brand-red",
          },
        ]
      : [];

  const discountItems: KeyValuePairProps[] = [
    ...(discounts || []),
    ...(discountsLength || []),
    ...addonsDiscountItems,
  ];

  // Total of the discounts actually listed (same types as the rows above), so
  // the collapsed header matches the expanded content.
  const dietDiscountTotal = (pricing?.discounts ?? [])
    .filter((d) => d.type === "automatic" || d.type === "diet_length")
    .reduce((sum, d) => sum + d.amount.gross, 0);
  const discountTotal = dietDiscountTotal + addonsDiscount;

  const selectedDates = form.watch("dates") ?? [];
  const formattedDeliveryDate = getDeliveryDate(selectedDates, locale);

  const tooltipErrors = () => (
    <ul>
      {errors.map((error) => (
        <li key={error}>{error}</li>
      ))}
    </ul>
  );
  const tooltipDeliveryInfo = () => (
    <span className="font-medium text-sm">
      {t("cart.configuration.deliveryTooltipWithoutHours", {
        deliveryDate: formattedDeliveryDate,
      })}
    </span>
  );

  const dirtyCheck = (field: keyof ConfigureDietFormType) =>
    form.getFieldState(field).isDirty;

  const dirty =
    dirtyCheck("dates") ||
    dirtyCheck("kcal") ||
    dirtyCheck("meals") ||
    dirtyCheck("variant");

  const { sendCustomizeDietEndEvent, isCustomizeDietEndSent } =
    useViewItemEvent({
      item: diet,
      configuration: { dates: form.watch("dates"), kcal: form.watch("kcal") },
      // Diet price, as this event has always reported — addons are a separate
      // decision for analytics, not part of this contract fix.
      price: dietTotal,
      end: !(pending || !isValid || !dirty),
      loading,
    });

  return (
    <div className="flex flex-col gap-y-4 lg:gap-y-2">
      <div className="order-2 lg:order-none">
        <Card className="relative order-2 overflow-hidden border bg-background p-0 md:sticky md:order-1">
          <div className="flex items-center gap-2 p-4 font-medium text-brand-green1-300 text-xl">
            <DocIcon className="size-6" />
            {t("cart.configuration.summary")}
          </div>

          <SummaryDietCard
            cateringName={formatCateringName(diet.catering.name)}
            days={daysQuantity}
            imageUrl={diet.imageUrl}
            kcal={Number(kcal) || 0}
            meals={form.watch("meals")?.length ?? 0}
            name={formatDietName(diet.name)}
          />

          <SummaryDayPriceRow
            className="p-4"
            dailyPrice={dailyPrice}
            days={daysQuantity}
            discounted={dailyPriceDiscounted}
            loading={loading}
            lowestPrice={omnibusPromotion?.lowestPrice}
            total={dietTotal}
          />

          {/* Collapsed on load everywhere — the design frames only show the
              expanded state to illustrate the contents. */}
          <Accordion className="w-full" type="multiple">
            {diet.id && (
              <SummaryAddonsSection
                dietId={diet.id}
                cateringName={formatCateringName(diet.catering.name)}
                subItemsPricing={subItemsPricing}
              />
            )}
          </Accordion>

          {discountItems.length > 0 && (
            <div className="flex flex-col border-t p-4 pb-0">
              <div className="flex items-center justify-between gap-2 pb-2 font-medium text-base">
                <span className="text-brand-green1-300">
                  {t("cart.configuration.totalPrice")}
                </span>
                <span className="text-brand-green1-300 line-through decoration-current">
                  {loading ? (
                    <BounceLoader className="h-4" />
                  ) : (
                    getLocalePrice({ price: regularTotalPrice })
                  )}
                </span>
              </div>
              <Accordion className="w-full" type="multiple">
                <SummaryDiscountSection
                  className="border-t-0"
                  contentClassName="px-0 pb-4"
                  items={discountItems}
                  label={t("cart.configuration.discount.label")}
                  total={`-${getLocalePrice({ price: discountTotal })}`}
                  triggerClassName="p-0 pb-3"
                />
              </Accordion>
            </div>
          )}

          <div className="flex flex-col gap-1 border-t p-4">
            <div className="flex items-center justify-between font-bold">
              <span className="text-brand-green1-300">
                {t("cart.configuration.totalToPay")}
              </span>
              <span className="text-brand-violet text-lg">
                {loading ? (
                  <BounceLoader className="h-7" />
                ) : (
                  getLocalePrice({ price: totalPrice ?? 0 })
                )}
              </span>
            </div>
            <div className="flex items-center justify-end gap-1">
              <DeliveryCost {...diet.catering.deliveryCosts} />
              {locale === "cz" && (
                <TooltipWrapper
                  align="end"
                  className="w-62 px-4 py-3"
                  content={tooltipDeliveryInfo()}
                  sideOffset={10}
                >
                  <IconInfoCircle className="mb-0.5 size-4" />
                </TooltipWrapper>
              )}
            </div>
            <TooltipWrapper
              active={(!isValid || !isDirty) && errors.length > 0}
              content={tooltipErrors()}
            >
              <Button
                className="mt-2 h-14 w-full text-brand-green1-300"
                data-pw="add-to-cart-button"
                disabled={pending}
                form="configure-diet-form"
                onClick={() => {
                  if (!isCustomizeDietEndSent) {
                    sendCustomizeDietEndEvent();
                  }
                }}
                type="submit"
                variant="secondary"
              >
                <div className="flex items-center gap-2.5">
                  <span>
                    {edit
                      ? t("cart.configuration.save")
                      : t("cart.configuration.addToCart")}
                  </span>
                  {pending ? (
                    <ButtonLoadingIcon loading={pending} />
                  ) : (
                    <IconArrowNarrowRight stroke={1.25} />
                  )}
                </div>
              </Button>
            </TooltipWrapper>
          </div>

          {loading && (
            <div className="absolute top-0 left-0 grid h-full w-full place-items-center rounded-lg bg-brand-green1-300/10">
              <Loader />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
